import React, { useMemo, useState, useEffect } from 'react';
import {HomeOutlined, UnorderedListOutlined, CheckOutlined, DownloadOutlined, ExclamationCircleFilled} from '@ant-design/icons';
import {Form, Row, Col, Divider, Button, message, Select, Switch, DatePicker, Input, Table, Typography, Space, Card, Tag, Modal} from 'antd';
import dayjs from 'dayjs';
import {
  getListAreas,
  getEmpleados,
  getUMA,
  getDepartamentos,
  getEmpleadosByDepartamento,
  getEmpleadosByArea,
  getIncapacitados,
  getSalarioDiarioIntegrado,
  aplicarCalculoIntegradoEmpleado,
  getEmpleadosActivos
} from '../../../../apis/CalculoIntegradoApi';
import PageApp from '../../../Page';

const { TextArea } = Input;
const { Title, Text } = Typography;

const routes = [
  {
    href: '',
    title: (
      <>
        <HomeOutlined />
        <span>Inicio</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <span>Recursos Humanos</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <span>Procesos</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Cálculo de Integrados</span>
      </>
    ),
  },
];

export default function CalculoIntegradosView() {
  const [form] = Form.useForm();

  const [uma, setUma] = useState(0);
  const [porcentaje, setPorcentaje] = useState(40);
  const [fijo, setFijo] = useState(uma * 25); // El maximo es 25 veces el uma

  const [loadingPrevio, setLoadingPrevio] = useState(false);
  const [loadingAplicar, setLoadingAplicar] = useState(false);

  const [dataPrevio, setDataPrevio] = useState([]);

  const [areasOptions, setAreasOptions] = useState([{ label: 'Seleccionar Area', value: '' }]);
  const [departamentosOptions, setDepartamentosOptions] = useState([{ label: 'Seleccionar Departamento', value: '' }]);

  const renderErrorContentWithLinks = (errorMessage) => {
    const messageText = errorMessage || 'No se recibió detalle del error.';

    if (typeof messageText !== 'string') {
      return messageText;
    }

    const syncLabel = 'sincronizar con Odoo';
    const configLabel = 'configuración de integraciones';
    const hasSyncLabel = messageText.includes(syncLabel);
    const hasConfigLabel = messageText.includes(configLabel);

    if (!hasSyncLabel && !hasConfigLabel) {
      return messageText;
    }

    const linksByLabel = {
      [syncLabel]: '/admin/configuracion_aplicacion?syncAll=false&models=hr.employee',
      [configLabel]: '/admin/integrados',
    };

    const labels = Object.keys(linksByLabel);
    const regex = new RegExp(`(${labels.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
    const parts = messageText.split(regex);

    return (
      <span>
        {parts.map((part, idx) => {
          const href = linksByLabel[part];
          if (!href) {
            return <React.Fragment key={`txt-${idx}`}>{part}</React.Fragment>;
          }

          return (
            <a key={`lnk-${idx}`} href={href}>
              {part}
            </a>
          );
        })}
      </span>
    );
  };

  const formatTwoDecimals = (value) => {
    const num = Number(value || 0);
    return Number.isFinite(num)
      ? num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';
  };

  const round2 = (value) => {
    const num = Number(value || 0);
    return Number.isFinite(num) ? Math.round(num * 100) / 100 : 0;
  };

  const hayCambioReal = (row) =>
    row?.estado_calculo !== 'error' &&
    round2(row?.salario_integrado_nuevo) !== round2(row?.salario_integrado_anterior);

  const formatNoDecimals = (value) => {
    const num = Number(value || 0);
    return Number.isFinite(num) ? Math.trunc(num).toLocaleString('es-MX') : '0';
  };

  const getApiErrorMessage = (response, fallback = 'Ocurrió un error al procesar el empleado.') => {
    if (!response) return fallback;

    if (typeof response === 'string') return response;

    if (Array.isArray(response.errors) && response.errors.length) {
      const first = response.errors[0];
      if (typeof first === 'string') return first;
      if (first?.msg) return first.msg;
      if (first?.message) return first.message;
    }

    if (response?.message) return response.message;
    if (response?.raw?.message) return response.raw.message;
    if (response?.errors?.message) return response.errors.message;

    return fallback;
  };

  const isElegibleParaIntegrado = (empleado, fechaSeleccionada) => {
    const fechaRef = dayjs(fechaSeleccionada).startOf('day');

    const entryDate = empleado?.nomina_entry_date
      ? dayjs(empleado.nomina_entry_date).startOf('day')
      : null;
    const contractStart = empleado?.nomina_contract_start_date
      ? dayjs(empleado.nomina_contract_start_date).startOf('day')
      : null;
    const contractEnd = empleado?.nomina_contract_end_date
      ? dayjs(empleado.nomina_contract_end_date).startOf('day')
      : null;
    const leavingDate = empleado?.nomina_leaving_date
      ? dayjs(empleado.nomina_leaving_date).startOf('day')
      : null;

    // Nuevas reglas: entrada/inicio de contrato obligatorias y con anio <= anio de calculo.
    if (!entryDate || !contractStart) return false;
    if (entryDate.year() > fechaRef.year()) return false;
    if (contractStart.year() > fechaRef.year()) return false;

    // Si existen fechas de fin/baja, deben ser mayores a la fecha de calculo.
    if (contractEnd && contractEnd.valueOf() <= fechaRef.valueOf()) return false;
    if (leavingDate && leavingDate.valueOf() <= fechaRef.valueOf()) return false;

    return true;
  };

  const initialValues = {
    movimiento: 'CAMBIO INTEGRADO',
    fecha: dayjs(),
    departamento: '',
    area: '',
    rangoEmpleados: '',
    comentarios: '',
    sinIncapacitados: true,
  };

  const loadAreas = async () => {
    try {
      const resAreas = await getListAreas();
      if (resAreas?.ok && Array.isArray(resAreas.payload)) {
        const opts = resAreas.payload.map((a) => ({
          label: a.text,
          value: a.value,
        }));
        setAreasOptions([{ label: 'Seleccionar Area', value: '' }, ...opts]);
      }
    } catch (err) {
      message.error('Error cargando áreas');
    }
  };

  const loadDepartamentos = async () => {
    try {
      const res = await getDepartamentos();

      if (res?.ok && Array.isArray(res.payload)) {
        const opts = res.payload.map((d) => ({
          label: d.text,
          value: d.odoo_id,
        }));

        setDepartamentosOptions([
          { label: 'Seleccionar Departamento', value: '' },
          ...opts,
        ]);
      }
    } catch (err) {
      message.error('Error cargando departamentos');
    }
  };

  const loadUMAValue = async () => {
    try {
      const res = await getUMA();

      if (res?.ok && res.payload?.monto != null) {
        const montoNum = Number(res.payload.monto);
        if (!Number.isNaN(montoNum)) {
          setUma(montoNum);
        } else {
          message.warning('El valor de UMA recibido no es numérico.');
        }
      } else {
        message.warning('No se pudo obtener el valor de UMA.');
      }
    } catch (err) {
      message.error('Error obteniendo el valor de UMA.');
    }
  };

  useEffect(() => {
    loadUMAValue();
    loadAreas();
    loadDepartamentos();
  }, []);

  useEffect(() => {
    setFijo(uma * 25);
  }, [uma]);

  const onMostrarPrevio = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      let ids = [];

      // Si escribió IDs manualmente → prioridad
      if (values.rangoEmpleados) {
        ids = (values.rangoEmpleados || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => Number(s))
          .filter((n) => !Number.isNaN(n));

        // Si no hay rango pero sí departamento
      } else if (values.departamento) {
        const deptId = values.departamento;
        try {
          const resDept = await getEmpleadosByDepartamento(deptId);
          if (resDept?.ok && Array.isArray(resDept.payload)) {
            ids = resDept.payload
              .map((e) => Number(e.id))
              .filter((n) => !Number.isNaN(n));
          } else {
            message.warning('No se pudieron obtener empleados para ese departamento.');
          }
        } catch (err) {
          message.error('Error obteniendo empleados por departamento.');
        }

        // Si no hay rango ni departamento, pero sí área
      } else if (values.area) {
        const areaId = values.area;

        try {
          const resArea = await getEmpleadosByArea(areaId);
          if (resArea?.ok && Array.isArray(resArea.payload)) {
            ids = resArea.payload
              .map((e) => Number(e.id))
              .filter((n) => !Number.isNaN(n));
          } else {
            message.warning('No se pudieron obtener empleados para esa área.');
          }
        } catch (err) {
          message.error('Error obteniendo empleados por área.');
        }
      } else {
        // Sin filtros: traer todos los empleados activos
        try {
          const resActivos = await getEmpleadosActivos();
          if (resActivos?.ok && Array.isArray(resActivos.payload)) {
            ids = resActivos.payload
              .map((e) => Number(e.value ?? e.id))
              .filter((n) => !Number.isNaN(n));
            if (!ids.length) {
              message.warning('No se encontraron empleados activos.');
            }
          } else {
            message.warning('No se pudieron obtener empleados activos.');
          }
        } catch (err) {
          message.error('Error obteniendo empleados activos.');
        }
      }

      // Si se marcó "No incluir Incapacitados", filtrarlos considerando la fecha seleccionada
      if (values.sinIncapacitados && ids.length) {
        try {
          const resInc = await getIncapacitados();
          if (resInc?.ok && Array.isArray(resInc.payload)) {
            const selectedMs = dayjs(values.fecha).startOf('day').valueOf();

            // Un empleado está incapacitado en la fecha si: inicio <= fecha y (fin es nulo o fin >= fecha)
            const incapacitadosActivos = resInc.payload.filter((inc) => {
              const iniStr = inc.fecha_inicial;
              const finStr = inc.fecha_final;

              const iniMs = iniStr ? dayjs(iniStr).startOf('day').valueOf() : null;
              const finMs = finStr ? dayjs(finStr).startOf('day').valueOf() : null;

              const started = iniMs != null ? iniMs <= selectedMs : true;
              const notFinished = finMs == null || finMs >= selectedMs;
              return started && notFinished;
            });

            const incapacitadosIds = incapacitadosActivos
              .map((inc) => Number(inc.empleado_id))
              .filter((n) => !Number.isNaN(n));

            const incapacitadosSet = new Set(incapacitadosIds);
            ids = ids.filter((id) => !incapacitadosSet.has(id));

            if (!ids.length) {
              message.warning('Todos los empleados seleccionados están incapacitados en la fecha indicada.');
            }
          }
        } catch (err) {
          message.error('Error obteniendo empleados incapacitados.');
        }
      }

      if (!ids.length) {
        message.warning('Sin filtros se toman todos los empleados activos; si aplicaste un filtro, este no tiene empleados válidos. Ajusta el filtro o déjalo vacío.');
        return;
      }

      setLoadingPrevio(true);

      // Se usan los IDs finales contra getEmpleados
      const resEmp = await getEmpleados(ids);

      if (!resEmp?.ok || !Array.isArray(resEmp.payload)) {
        message.error('No se pudieron obtener los empleados.');
        setDataPrevio([]);
        return;
      }

      const fechaSeleccionada = dayjs(values.fecha).startOf('day');
      const fechaMs = fechaSeleccionada.valueOf();
      const fechaActualStr = fechaSeleccionada.format('YYYY-MM-DD');

      const empleadosFiltrados = resEmp.payload.filter((e) =>
        isElegibleParaIntegrado(e, fechaSeleccionada)
      );

      if (!empleadosFiltrados.length) {
        message.warning('No hay empleados elegibles para la fecha seleccionada.');
        setDataPrevio([]);
        return;
      }

      // Mapeo empleados base
      const empleados = empleadosFiltrados.map((e) => ({
        id: e.id,
        nombre: e.name,
        fecha_ingreso: e.nomina_entry_date || null,
        salario_mensual: Number(e.nomina_monthly_salary || 0),
        salario_diario: Number(e.nomina_daily_salary || 0),
        factor_integracion: Number(e.nomina_integration_factor || 1),
        variable: Number(e.nomina_variable_amount || 0),
        fijo: Number(e.nomina_fixed_amount || 0),
        fijo_topado: Number(e.fijo_topado || 0),
        salario_integrado_anterior: Number(e.nomina_integrated_salary || 0),
        __raw: e,
      }));

      // Obtener cálculo completo desde backend para cada empleado
      const empleadosConCalculo = await Promise.all(
        empleados.map(async (emp) => {
          try {
            const resSDI = await getSalarioDiarioIntegrado(emp.id, fechaActualStr);
            if (resSDI?.ok && resSDI.payload) {
              const payload = resSDI.payload;
              const det = payload.detalle || {};
              const salarioIntegradoNuevo = Number(payload.salario_diario_integrado || 0);
              const factorValue = det.factor_integracion != null ? Number(det.factor_integracion) : emp.factor_integracion;

              return {
                ...emp,
                factor_integracion: factorValue,
                salario_integrado_nuevo: salarioIntegradoNuevo,
                tabla: det.tabla || '',
                antiguedad_calc: det.antiguedad,
                uma_calc: det.uma,
                estado_calculo: 'ok',
                error_calculo: null,
              };
            }

            const errorMsg = getApiErrorMessage(resSDI, `No se pudo calcular el salario integrado de ${emp.nombre}.`);

            return {
              ...emp,
              salario_integrado_nuevo: null,
              tabla: '',
              antiguedad_calc: null,
              uma_calc: null,
              estado_calculo: 'error',
              error_calculo: errorMsg,
            };

          } catch (error) {
            return {
              ...emp,
              salario_integrado_nuevo: null,
              tabla: '',
              antiguedad_calc: null,
              uma_calc: null,
              estado_calculo: 'error',
              error_calculo: `Ocurrió un error al calcular el salario integrado para ${emp.nombre}.`,
            };
          }
        })
      );

      const empleadosVisibles = empleadosConCalculo.filter(
        (row) => row?.estado_calculo === 'error' || hayCambioReal(row)
      );
      const totalConCambio = empleadosVisibles.filter((e) => e?.estado_calculo !== 'error').length;
      const totalErrores = empleadosVisibles.filter((e) => e?.estado_calculo === 'error').length;
      const totalSinCambio = empleadosConCalculo.length - empleadosVisibles.length;
      const omitidosMsg =
        totalSinCambio > 0 ? ` ${totalSinCambio} sin cambio en salario integrado (omitido(s)).` : '';

      setDataPrevio(empleadosVisibles);

      if (totalErrores > 0 && totalConCambio > 0) {
        message.warning(
          `Previo calculado parcialmente: ${totalConCambio} con cambio real, ${totalErrores} con error.${omitidosMsg}`
        );
      } else if (totalErrores > 0) {
        message.error(`No se pudo calcular el previo. ${totalErrores} empleado(s) con error.${omitidosMsg}`);
      } else if (totalConCambio > 0) {
        message.success(`Previo recalculado: ${totalConCambio} empleado(s) con cambio real en salario integrado.${omitidosMsg}`);
      } else {
        message.info('Previo recalculado: ningún empleado tiene cambios reales en el salario integrado.');
      }
    } catch (err) {
      message.error('Error mostrando el previo de cálculo.');
    } finally {
      setLoadingPrevio(false);
    }
  };

  const onAplicarCambios = async () => {
    try {
      if (!dataPrevio.length) {
        message.warning('No hay empleados en el previo para aplicar cambios.');
        return;
      }

      const values = form.getFieldsValue();
      if (!values.fecha) {
        message.error('Selecciona una fecha antes de aplicar cambios.');
        return;
      }

      const empleadosAplicables = dataPrevio.filter((row) => hayCambioReal(row));

      if (!empleadosAplicables.length) {
        message.info('No hay empleados con cambios reales para aplicar.');
        return;
      }

      // Mostrar modal de confirmación
      Modal.confirm({
        title: '¿Confirmar aplicación de cambios?',
        icon: <ExclamationCircleFilled />,
        content: (
          <div>
            <p>Se aplicarán los cambios de salario integrado a <strong>{empleadosAplicables.length}</strong> empleado(s).</p>
            <p>Esta acción modificará los registros en el sistema.</p>
            <p>¿Deseas continuar?</p>
          </div>
        ),
        okText: 'Sí, aplicar cambios',
        okType: 'primary',
        cancelText: 'Cancelar',
        onOk: async () => {
          const fechaSeleccionada = dayjs(values.fecha).startOf('day');
          const fechaActualStr = fechaSeleccionada.format('YYYY-MM-DD');

          setLoadingAplicar(true);

          try {
            // Llamar al backend por cada empleado con cambio real
            const results = await Promise.allSettled(
              empleadosAplicables.map((row) => aplicarCalculoIntegradoEmpleado(row.id, fechaActualStr, values.comentarios))
            );

            let ok = 0;
            let fail = 0;

            results.forEach((r) => {
              if (r.status === 'fulfilled' && r.value?.ok) {
                ok += 1;
              } else {
                fail += 1;
              }
            });

            if (ok && !fail) {
              message.success(`Cambios aplicados correctamente para ${ok} empleado(s).`);
            } else if (ok && fail) {
              message.warning(`Se aplicaron cambios a ${ok} empleado(s), pero falló en ${fail}. Revisa la consola/logs.`);
            } else {
              message.error('No se pudo aplicar cambios a ningún empleado.');
            }
          } catch (err) {
            message.error('Error al aplicar cambios.');
          } finally {
            setLoadingAplicar(false);
          }
        },
      });
    } catch (err) {
      message.error('Error al preparar la aplicación de cambios.');
    }
  };

  const exportToExcel = () => {
    if (!dataPrevio.length) {
      message.warning('No hay datos para exportar.');
      return;
    }

    // Construir tabla HTML con estilos inline
    let htmlTable = `
      <table border="1" cellpadding="5" cellspacing="0">
        <thead>
          <tr style="background-color: #f0f0f0; font-weight: bold;">
            <th>ID</th>
            <th>Empleado</th>
            <th>Integrado Anterior</th>
            <th>Integrado Nuevo</th>
            <th>Fecha Ingreso</th>
            <th>Antigüedad</th>
            <th>Tabla</th>
            <th>Mensual</th>
            <th>Diario</th>
            <th>Factor</th>
            <th>Variable</th>
            <th>Fijo</th>
          </tr>
        </thead>
        <tbody>
    `;

    dataPrevio.forEach((row) => {
      const fechaIngreso = row.fecha_ingreso ? dayjs(row.fecha_ingreso).format('DD-MMM-YY') : '';
      const a =
        row.antiguedad_calc != null
          ? Number(row.antiguedad_calc)
          : dayjs().diff(dayjs(row.fecha_ingreso), 'year', true);
      const years = Number.isFinite(a) ? Math.trunc(a) : 0;
      const antiguedad = years === 0 ? 1 : years;

      htmlTable += `
        <tr>
          <td>${row.id}</td>
          <td>${row.nombre}</td>
          <td>$ ${formatTwoDecimals(row.salario_integrado_anterior)}</td>
          <td>$ ${formatTwoDecimals(row.salario_integrado_nuevo)}</td>
          <td>${fechaIngreso}</td>
          <td>${antiguedad.toLocaleString()}</td>
          <td>${row.tabla || ''}</td>
          <td>$ ${formatTwoDecimals(row.salario_mensual)}</td>
          <td>$ ${formatTwoDecimals(row.salario_diario)}</td>
          <td>${row.factor_integracion}</td>
          <td>${formatTwoDecimals(row.variable)}</td>
          <td>${formatTwoDecimals(row.fijo)}</td>
        </tr>
      `;
    });

    htmlTable += `
        </tbody>
      </table>
    `;

    // Crear Blob y descargar
    const blob = new Blob([htmlTable], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Calculo_Integrados_${dayjs().format('YYYY-MM-DD')}.xls`;
    link.click();
    URL.revokeObjectURL(url);

    message.success('Archivo exportado correctamente.');
  };

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        fixed: 'left',
        width: 90,
        align: 'center',
        render: (val) => <Text strong>{val}</Text>,
        sorter: (a, b) => Number(a.id || 0) - Number(b.id || 0),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Empleado',
        dataIndex: 'nombre',
        key: 'nombre',
        fixed: 'left',
        width: 260,
        render: (val) => <Text strong>{val}</Text>,
        sorter: (a, b) => (a.nombre || '').localeCompare(b.nombre || ''),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Estado',
        key: 'estado_calculo',
        width: 90,
        align: 'center',
        render: (_, r) => {
          if (r?.estado_calculo === 'error') {
            return (
              <Button
                type="text"
                danger
                icon={<ExclamationCircleFilled />}
                onClick={() => {
                  Modal.error({
                    title: `No se pudo calcular para ${r.nombre}`,
                    content: renderErrorContentWithLinks(r.error_calculo),
                  });
                }}
              />
            );
          }

          return <Tag color="green">OK</Tag>;
        },
      },
      {
        title: () => (
          <Space direction="vertical" size={0}>
            <Text strong>Valor del UMA</Text>
            <Text type="secondary">{uma}</Text>
          </Space>
        ),
        children: [
          {
            title: 'Integrado Anterior',
            dataIndex: 'salario_integrado_anterior',
            key: 'salario_integrado_anterior',
            width: 180,
            align: 'center',
            render: (v) => `$ ${formatTwoDecimals(v)}`,
          },
          {
            title: 'Integrado Nuevo',
            dataIndex: 'salario_integrado_nuevo',
            key: 'salario_integrado_nuevo',
            width: 200,
            align: 'center',
            render: (v) => (
              v == null ? (
                <Text type="secondary">-</Text>
              ) : (
              <div
                style={{
                  background: '#FFF7E6',
                  border: '1px solid #FFE7BA',
                  padding: 4,
                  borderRadius: 6,
                  display: 'inline-block',
                  minWidth: 120,
                  textAlign: 'center',
                }}
              >
                <Text strong>$ {formatTwoDecimals(v)}</Text>
              </div>
              )
            ),
          },
        ],
      },
      {
        title: 'Fecha Ingreso',
        dataIndex: 'fecha_ingreso',
        key: 'fecha_ingreso',
        align: 'center',
        width: 120,
        render: (v) => (v ? dayjs(v).format('DD-MMM-YY') : ''),
      },
      {
        title: 'Antigüedad',
        key: 'antiguedad',
        width: 110,
        align: 'center',
        render: (_, r) => {
          const a =
            r.antiguedad_calc != null
              ? Number(r.antiguedad_calc)
              : dayjs().diff(dayjs(r.fecha_ingreso), 'year', true);
          const years = Number.isFinite(a) ? Math.trunc(a) : 0;
          const shown = years === 0 ? 1 : years;
          return <Text>{shown.toLocaleString()}</Text>;
        },
      },
      {
        title: 'Tabla',
        dataIndex: 'tabla',
        key: 'tabla',
        width: 90,
        align: 'center',
        render: (v) => (v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">-</Text>),
      },
      {
        title: 'Mensual',
        dataIndex: 'salario_mensual',
        key: 'salario_mensual',
        width: 140,
        align: 'center',
        render: (v) => `$ ${formatTwoDecimals(v)}`,
      },
      {
        title: 'Diario',
        dataIndex: 'salario_diario',
        key: 'salario_diario',
        width: 140,
        align: 'center',
        render: (v) => `$ ${formatTwoDecimals(v)}`,
      },
      {
        title: 'Factor',
        dataIndex: 'factor_integracion',
        key: 'factor_integracion',
        width: 140,
        align: 'center',
      },
      {
        title: 'Variable',
        dataIndex: 'variable',
        key: 'variable',
        width: 100,
        align: 'center',
        render: (v) => formatTwoDecimals(v),
      },
      {
        title: 'Fijo',
        dataIndex: 'fijo',
        key: 'fijo',
        width: 100,
        align: 'center',
        render: (v) => formatTwoDecimals(v),
      },
    ],
    [uma]
  );

  return (
    <PageApp 
      routes={routes}
      title="Cálculo de Integrados"
      description="Cálculo y actualización del salario diario integrado de empleados seleccionados."
    >
      <div style={{ padding: 16 }}>
        <Card>
          <Form form={form} layout="vertical" initialValues={initialValues}>
            <Row gutter={[16, 8]}>
              <Col xs={24} md={8} lg={6}>
                <Form.Item label="Movimiento" name="movimiento">
                  <Input readOnly />
                </Form.Item>
              </Col>

              <Col xs={24} md={8} lg={6}>
                <Form.Item
                  label="Fecha"
                  name="fecha"
                  rules={[{ required: true, message: 'Selecciona una fecha' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>

              <Col xs={24} md={8} lg={6}>
                <Form.Item label="Departamento" name="departamento">
                  <Select options={departamentosOptions} />
                </Form.Item>
              </Col>

              <Col xs={24} md={8} lg={6}>
                <Form.Item label="Área" name="area">
                  <Select options={areasOptions} />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label="Rango de Empleados" name="rangoEmpleados">
                  <Input placeholder="Ej. 1,2,5,7" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label="Comentarios" name="comentarios">
                  <TextArea
                    rows={2}
                    placeholder="Comentarios del cambio de integrado"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  label="No incluir Incapacitados"
                  name="sinIncapacitados"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* KPIs superiores */}
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card size="small">
                  <Space direction="vertical">
                    <Text type="secondary">Valor UMA</Text>
                    <Input type="number" value={uma} readOnly suffix="MXN" />
                  </Space>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card size="small">
                  <Space direction="vertical">
                    <Text type="secondary">Fijo topado</Text>
                    <Input
                      type="number"
                      value={fijo}
                      onChange={(e) => setFijo(Number(e.target.value || 0))}
                      readOnly
                      suffix="MXN"
                    />
                  </Space>
                </Card>
              </Col>
            </Row>

            <Space style={{ marginTop: 16 }}>
              <Button
                icon={<UnorderedListOutlined />}
                onClick={onMostrarPrevio}
                loading={loadingPrevio}
                type="default"
              >
                Mostrar Previo
              </Button>

              <Button
                icon={<CheckOutlined />}
                type="primary"
                onClick={onAplicarCambios}
                loading={loadingAplicar}
              >
                Aplicar Cambios
              </Button>
            </Space>
          </Form>
        </Card>

        <Divider />

        <Card
          title="Resultado del Previo de Cálculo"
          size="small"
          extra={
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              disabled={!dataPrevio.length}
            >
              Exportar a Excel
            </Button>
          }
        >
          <Table
            rowKey="id"
            dataSource={dataPrevio}
            columns={columns}
            size="small"
            bordered
            scroll={{ x: 1200 }}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{
              emptyText:
                'Usa “Mostrar Previo” para ver el cálculo con los parámetros actuales',
            }}
          />
        </Card>
      </div>
    </PageApp>
  );
}